package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.getPlatform
import com.shadowings.gero.presentation.component.GeroButton
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.LocalIsDarkMode
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import com.shadowings.gero.util.SoundPlayer
import com.shadowings.gero.util.rememberShareManager
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.U28
import gero.composeapp.generated.resources.daily_fire_bonus_coins
import gero.composeapp.generated.resources.daily_fire_desc
import gero.composeapp.generated.resources.daily_fire_strike_1
import gero.composeapp.generated.resources.daily_fire_strike_many
import gero.composeapp.generated.resources.daily_fire_title
import gero.composeapp.generated.resources.fantastic_btn
import gero.composeapp.generated.resources.share_btn_text
import gero.composeapp.generated.resources.share_streak_many
import gero.composeapp.generated.resources.share_streak_one
import gero.composeapp.generated.resources.share_title
import gero.composeapp.generated.resources.top_bar_fire_desc
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun StreakCelebrationPagePreview() {
    AppTheme {
        Surface {
            StreakCelebrationPageComposable(
                strikeCount = 3,
                onContinue = {}
            )
        }
    }
}

// Rendered as a page inside the quiz's own HorizontalPager (see QuizScreen.kt), not as a
// dialog: a dialog triggered by persisted/synced "show it now" state is what caused it to
// loop forever when cloud sync raced the local dismiss. Shown once, inline, right after the
// quiz that earned it — nothing to persist or re-sync.
@Composable
fun StreakCelebrationPageComposable(
    strikeCount: Int,
    isVisible: Boolean = true,
    onContinue: () -> Unit
) {
    val isDarkMode = LocalIsDarkMode.current
    val deviceInfo = rememberDeviceInfo()

    // The pager composes the next page ahead of the current one (prefetch), so a plain
    // LaunchedEffect(Unit) fires while this page is still off-screen — gate on isVisible,
    // same as FinalQuizScreenComposable's own sound/confetti trigger.
    LaunchedEffect(isVisible) {
        if (isVisible) {
            SoundPlayer.playStreak()
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier
                .padding(24.dp)
                .fillMaxSize()
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.SpaceBetween
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                Text(
                    text = stringResource(Res.string.daily_fire_title),
                    style = MaterialTheme.typography.headlineLarge.copy(fontWeight = FontWeight.Bold),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth()
                )
            }
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                val fontScale = LocalDensity.current.fontScale
                if ((fontScale < 1.49f || deviceInfo.isPortrait) && deviceInfo.deviceClass != DeviceClass.PHONE_SMALL) {
                    Box(
                        modifier = Modifier
                            .size(96.dp)
                            .background(
                                color = MaterialTheme.colorScheme.primaryContainer,
                                shape = RoundedCornerShape(16.dp)
                            )
                            .padding(16.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            painter = painterResource(Res.drawable.U28),
                            contentDescription = stringResource(Res.string.top_bar_fire_desc),
                            modifier = Modifier.fillMaxSize(),
                            colorFilter = if (isDarkMode) {
                                ColorFilter.tint(MaterialTheme.colorScheme.onPrimaryContainer)
                            } else {
                                null
                            }
                        )
                    }
                }
            }
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                Text(
                    text = stringResource(Res.string.daily_fire_desc),
                    style = MaterialTheme.typography.headlineSmall,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth()
                )

                Text(
                    text = if (strikeCount == 1) {
                        stringResource(Res.string.daily_fire_strike_1, strikeCount)
                    } else {
                        stringResource(Res.string.daily_fire_strike_many, strikeCount)
                    },
                    style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.SemiBold),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth()
                )
            }

            Text(
                text = stringResource(Res.string.daily_fire_bonus_coins),
                style = MaterialTheme.typography.headlineSmall.copy(
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary
                ),
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth()
            )

            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                // Not on web: the JS ShareManager can only copy the caption text (no native share
                // sheet), a degraded experience compared to Android/iOS's real share sheet.
                if (!getPlatform().isWeb) {
                    val shareManager = rememberShareManager()
                    val shareTitle = stringResource(Res.string.share_title)
                    val shareText = if (strikeCount == 1) {
                        stringResource(Res.string.share_streak_one, strikeCount)
                    } else {
                        stringResource(Res.string.share_streak_many, strikeCount)
                    }

                    GeroButton(
                        modifier = Modifier.height(48.dp).fillMaxWidth(),
                        title = stringResource(Res.string.share_btn_text),
                        testTagId = "strike_bonus_dialog_btn_share",
                        backgroundColor = MaterialTheme.colorScheme.secondaryContainer,
                        borderColor = MaterialTheme.colorScheme.secondary,
                        textColor = MaterialTheme.colorScheme.onSecondaryContainer
                    ) {
                        shareManager(shareText, null, shareTitle)
                    }
                    Spacer(Modifier.height(8.dp))
                }


                GeroButton(
                    modifier = Modifier.height(64.dp).fillMaxWidth(),
                    title = stringResource(Res.string.fantastic_btn),
                    testTagId = "strike_bonus_dialog_btn_ok"
                ) {
                    onContinue()
                }
            }
        }
    }
}
